/**
 * WL email step (guest-only email capture in the Variant B questionnaire).
 *
 * HL-11800 tested the step's POSITION (B, right before the Legal Consent page, won) and HL-11986
 * its DESIGN (C, the new design without the checklist, won). Both tests are closed (HL-12595):
 * every guest gets design C before Legal Consent.
 */

import { MainTracker } from "../PixelTrackers/MainTracker";
import { getWlFunnelVariant } from "./funnelMentionRedirects";

export type WlEmailStepPosition = "before_consent";
export const WL_EMAIL_STEP_POSITION: WlEmailStepPosition = "before_consent";

const EMAIL_GIVEN_SESSION_KEY = "wl_email_step_email_given";
const OAUTH_RETURN_SESSION_KEY = "wl_email_step_oauth_return";
// A Google round-trip takes seconds, not hours; anything older is a stale marker.
const OAUTH_RETURN_TTL_MS = 30 * 60 * 1000;

export type QuestionnaireVersion = "wl" | "wl3";
export const QUESTIONNAIRE_VERSION_SUPER_PROPERTY = "questionnaire_version";

/** The short V3 questionnaire (DE weight_loss_3, UK weight_loss_2) is "wl3"; everything else the current WL one. */
export const getQuestionnaireVersion = (mention: unknown, country?: string): QuestionnaireVersion =>
  getWlFunnelVariant(mention, country) === "wl3" ? "wl3" : "wl";

/**
 * HL-11986 §6: tell WL and WL3 events apart on every funnel event through Paid Order.
 * Registered once at questionnaire load for everyone, signed in or not, like
 * "Category Catalog".
 */
export const registerQuestionnaireVersionSuperProperty = (mention: unknown, country?: string): void => {
  MainTracker.superProperties({
    [QUESTIONNAIRE_VERSION_SUPER_PROPERTY]: getQuestionnaireVersion(mention, country),
  });
};

/** "Email already given in this funnel" — survives reload, dies with the tab. */
export const hasGivenWlEmail = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    return sessionStorage.getItem(EMAIL_GIVEN_SESSION_KEY) === "1";
  } catch {
    return false;
  }
};

export const markWlEmailGiven = (): void => {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(EMAIL_GIVEN_SESSION_KEY, "1");
  } catch {
    // Storage blocked (private mode) — the step may show again after reload; acceptable.
  }
};

/**
 * "Left the email step through the Google button" marker.
 *
 * Google OAuth is a full-page redirect, so the Variant B screen stack (React
 * state only) is lost. Written right before the redirect, read once when the
 * questionnaire loads again so it can land the user back on the step (guest
 * came back / cancelled) or on its continuation, the Legal Consent page
 * (signed in), instead of resuming from the first unanswered question. Scoped
 * to the questionnaire pathname and short-lived so a stale marker can never
 * steer another visit.
 */
export const markWlEmailStepOAuthReturn = (): void => {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(
      OAUTH_RETURN_SESSION_KEY,
      JSON.stringify({ path: window.location.pathname, ts: Date.now() })
    );
  } catch {
    // Storage blocked — the user resumes at the first unanswered page; acceptable.
  }
};

/** Pure read (never clears): true when this load is the return leg of the step's Google redirect. */
export const peekWlEmailStepOAuthReturn = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    const raw = sessionStorage.getItem(OAUTH_RETURN_SESSION_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { path?: unknown; ts?: unknown };
    if (parsed.path !== window.location.pathname) return false;
    if (typeof parsed.ts !== "number" || Date.now() - parsed.ts > OAUTH_RETURN_TTL_MS) return false;
    return true;
  } catch {
    return false;
  }
};

export const clearWlEmailStepOAuthReturn = (): void => {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(OAUTH_RETURN_SESSION_KEY);
  } catch {
    // ignore
  }
};
